import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { ArrowForward, DesktopWindowsOutlined, FileDownloadOutlined, VpnKeyOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useApp } from '../contexts/AppContext';
import { SoftwareOverviewLicense, summarizeSoftwareLicenses } from '../utils/softwareOverview';
import { SOFTWARE_PRODUCTS, softwareProductLabel } from '../utils/softwareProducts';

export default function SoftwareOverview({ refreshKey = 0 }: { refreshKey?: number }) {
  const navigate = useNavigate();
  const { state } = useApp();
  const canManage = ['administrator', 'reseller'].includes(state.auth.user?.role || '');
  const [licenses, setLicenses] = useState<SoftwareOverviewLicense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try { setLicenses((await api.get('/software-licenses')).data.data.licenses); }
    catch { setError(true); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load, refreshKey]);
  const summary = summarizeSoftwareLicenses(licenses);
  const numbers = [
    { label: 'Licenze attive', value: summary.active, query: 'ACTIVE', color: 'success.main' },
    { label: 'In scadenza · 30 giorni', value: summary.soon, query: 'SOON', color: 'warning.main' },
    { label: 'Scadute o sospese', value: summary.blocked, query: 'ATTENTION', color: summary.blocked ? 'error.main' : 'text.primary' },
  ];
  return <Paper component="section" aria-label="Software e licenze" sx={{ overflow: 'hidden', borderRadius: '20px' }}>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.1fr 1fr' } }}>
      <Box sx={{ p: { xs: 2.5, md: 3.5 }, background: 'linear-gradient(120deg, rgba(250,140,33,.10), transparent 85%)' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <VpnKeyOutlined sx={{ color: 'primary.main', fontSize: 20 }} />
          <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '.12em' }}>Software e licenze</Typography>
        </Stack>
        <Typography variant="h4" component="h2" sx={{ maxWidth: 470, fontSize: { xs: '1.7rem', md: '2rem' }, lineHeight: 1.2 }}>
          Dal software al primo avvio.
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1.5, mb: 2.5, maxWidth: 470 }}>
          {canManage ? 'Prepara la licenza, consegna il codice o il file e segui le postazioni autorizzate. Il cliente attiva NextCut, NextFrame e NextDrill con un programma compatibile.' : 'Controlla le licenze della tua azienda, le scadenze e i computer autorizzati. Scarica i pacchetti aggiornati dei tuoi software.'}
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          <Button variant="contained" endIcon={<ArrowForward />} onClick={() => navigate('/licenses')}>Apri licenze software</Button>
          <Button startIcon={<FileDownloadOutlined />} onClick={() => navigate('/software-updates')}>Download software</Button>
        </Stack>
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" aria-label="Licenze per software" sx={{ mt: 2.5, color: 'text.secondary' }}>
          <DesktopWindowsOutlined sx={{ fontSize: 16 }} />
          {SOFTWARE_PRODUCTS.map(product => <Chip key={product} label={softwareProductLabel(product)} size="small" variant="outlined" aria-label={'Apri licenze ' + softwareProductLabel(product)} onClick={() => navigate('/licenses?product=' + product)} />)}
        </Stack>
      </Box>
      <Box sx={{ p: { xs: 2.5, md: 3.5 }, borderLeft: { lg: '1px solid' }, borderTop: { xs: '1px solid', lg: 'none' }, borderColor: { xs: 'divider', lg: 'divider' }, minWidth: 0 }}>
        {error ? <Alert severity="warning" action={<Button color="inherit" onClick={() => void load()}>Riprova</Button>}>Licenze non disponibili. I dati non sono stati aggiornati.</Alert> : <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1.5, mb: 3 }}>
            {numbers.map(item => <Box key={item.query}>
              <Typography variant="h3" sx={{ color: item.color, fontVariantNumeric: 'tabular-nums' }}>{loading ? '—' : item.value}</Typography>
              <Button size="small" onClick={() => navigate(`/licenses?status=${item.query}`)} sx={{ p: 0, mt: .5, minWidth: 0, textAlign: 'left', justifyContent: 'flex-start', color: 'text.secondary', fontSize: 11, lineHeight: 1.4 }}>{item.label}</Button>
            </Box>)}
          </Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography fontWeight={700}>Da seguire</Typography>
            <Typography variant="caption" color="text.secondary">{loading ? 'Caricamento…' : `${summary.total} licenze totali`}</Typography>
          </Stack>
          {!loading && !summary.attention.length && <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>Nessuna scadenza o sospensione da verificare.</Typography>}
          {!loading && summary.attention.slice(0, 3).map(license => <Box key={license.id} sx={{ py: 1.2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700} noWrap>{license.company.name} · {license.reference}</Typography>
              <Typography variant="caption" color="text.secondary">{license.product}{!license.isPerpetual && license.expiresAt ? ` · ${new Date(license.expiresAt).toLocaleDateString('it-IT', { timeZone: 'UTC' })}` : ''}</Typography>
            </Box>
            <Chip size="small" variant="outlined" color={license.status === 'ACTIVE' ? 'warning' : 'error'} label={license.status === 'EXPIRED' ? 'Scaduta' : license.status === 'SUSPENDED' ? 'Sospesa' : 'In scadenza'} />
          </Box>)}
        </>}
      </Box>
    </Box>
  </Paper>;
}
